import React, {
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';
import { Button, message, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import JsPDF from 'jspdf';
import 'jspdf-autotable';
import { resizeImageRatio } from '@utils/resizeImageRatio';
import {
  formatCpfCnpj,
  formatCurrency,
  getImgInBase64,
  hexToRgb,
  maskPhone,
} from '@utils/formatters';
import SessionContext from '@providers/Session/SessionContext';
import api from '@services/api';

interface IPdfDetailedInvoice {
  customerId: string | undefined;
  invoiceId: string | undefined;
}

const PdfDetailedInvoice = ({ customerId, invoiceId }: IPdfDetailedInvoice) => {
  const { t } = useTranslation('invoices');
  const { logoImgName, mainColor, isAlgarDomain } = useContext(SessionContext);

  const mainColorInRGB = useMemo(
    () => hexToRgb(mainColor, '#3C4646'),
    [mainColor],
  );

  const [image, setImage] = useState<HTMLImageElement | any>();
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [downloadProgress, setDownloadProgress] = useState(false);

  const getInvoiceData = useCallback(
    () =>
      api.get(
        `/service-proxy/broker/${customerId}/invoices/${invoiceId}/summary`,
      ),
    [customerId, invoiceId],
  );

  useEffect(() => {
    if (logoImgName && !isAlgarDomain) {
      const { hostname } = window.location;
      const img = new Image();
      img.src = `/images/${hostname ?? logoImgName}`;

      img.onload = (e: any) => {
        const { width, height } = resizeImageRatio(
          e.target.width,
          e.target.height,
          32,
          32,
        );
        setImageWidth(width);
        setImageHeight(height);
      };
      setImage(img);
    }
  }, [isAlgarDomain, logoImgName]);

  const header = useCallback(
    (doc: JsPDF, data: any) => {
      const domain = data?.customer?.domain;

      if (!isAlgarDomain && domain && imageWidth && imageHeight) {
        doc.addImage(
          getImgInBase64(image),
          'PNG',
          15,
          15,
          imageWidth,
          imageHeight,
        );
      }

      const pageWidth =
        doc.internal.pageSize.width || doc.internal.pageSize.getWidth();

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      // @ts-ignore
      doc.text(t('pdf.header.simplifiedInvoice'), pageWidth / 2, 40, 'center');

      const subtitleLines = [
        {
          label: 'usePeriod',
          value: `${data.dataInicio} ${t('pdf.header.to')} ${data.dataTermino}`,
        },
        {
          label: 'dueDate',
          value: data.dataVencimento,
        },
        {
          label: 'customer',
          value: data.dsRazaoSocial,
        },
      ];

      let size = 54;
      subtitleLines.forEach(({ label, value }) => {
        const translatedLabel = `${t(`pdf.header.${label}`)}: `;

        doc.setFontSize(10);
        // label
        doc.setFont('helvetica', 'bold');
        doc.text(translatedLabel, 14, size);
        // value
        doc.setFont('helvetica', 'normal');
        doc.text(value, 14 + doc.getTextWidth(translatedLabel) + 2, size);

        size += 6;
      });
    },
    [image, imageHeight, imageWidth, isAlgarDomain, t],
  );

  const handleDocText = useCallback(
    (attrList: string[], joinMask = ' | ') =>
      attrList?.filter(attr => attr)?.join(joinMask),
    [],
  );

  const footer = useCallback(
    (doc: JsPDF, customer: any) => {
      if (customer) {
        let { cpfCnpj } = customer;
        const { name } = customer;
        if (cpfCnpj) {
          cpfCnpj = `CNPJ: ${formatCpfCnpj(customer.cpfCnpj)}`;
        }

        const lines = [
          {
            font: ['helvetica', 'bold'],
            text: handleDocText([name, cpfCnpj]),
          },
        ];

        if (customer.contact) {
          const { supportEmail, financialEmail } = customer.contact;
          let { phoneNumber } = customer.contact;

          if (phoneNumber) {
            phoneNumber = maskPhone(phoneNumber);
          }
          lines.push({
            font: ['helvetica', 'normal'],
            text: handleDocText([financialEmail, supportEmail, phoneNumber]),
          });
        }

        if (customer.address) {
          const { streetName, number, complement, neighborhood, city, state } =
            customer.address;
          lines.push({
            font: ['helvetica', 'normal'],
            text: `${handleDocText(
              [streetName, number, complement, neighborhood],
              ', ',
            )} | ${handleDocText([city, state], '-')}`,
          });
        }

        let size = 16; // Distance from the lines
        lines.forEach(line => {
          // @ts-ignore
          doc.setFont(...line.font);
          doc.text(
            line.text,
            14,
            // @ts-ignore
            doc.lastAutoTable.finalY > 260
              ? size
              : // @ts-ignore
                doc.lastAutoTable.finalY + size,
          );
          size += 6;
        });
      }
    },
    [handleDocText],
  );

  const handleTableData = useCallback(
    (data: any, columns: string[]) =>
      data?.map((item: any) =>
        columns?.map(col => {
          switch (col) {
            case 'nuValorUnitario':
            case 'nuValorTotal':
              return formatCurrency(item[col] ?? 0);
            case 'nuQtdItem':
              return item[col] ?? 0;
            default:
              return item[col] ?? '';
          }
        }),
      ),
    [],
  );

  const createTable = useCallback(
    ({
      doc,
      title,
      columns,
      data,
      foot,
    }: {
      doc: JsPDF;
      title: string;
      columns: string[];
      data: any[];
      foot: ReactNode;
    }) => {
      // @ts-ignore
      doc.autoTable({
        didParseCell: (table: any) => {
          if (table?.cell?.raw?.content === title) {
            // eslint-disable-next-line no-param-reassign
            table.cell.styles.fillColor = mainColor;
          }
        },
        showHead: 'firstPage',
        showFoot: 'lastPage',
        theme: 'grid',
        margin: { top: 75, bottom: 5 },
        didDrawPage: (didDrawData: any) => {
          // eslint-disable-next-line no-param-reassign
          didDrawData.settings.margin.top = 5;
        },
        headStyles: {
          halign: 'center',
          textColor: 80,
          fillColor: [240, 245, 245],
          lineWidth: 0.1,
          color: 'red',
        },
        footStyles: {
          halign: 'center',
          textColor: 80,
          fillColor: [240, 245, 245],
          lineWidth: 0.1,
        },
        bodyStyles: { halign: 'center' },
        head: [
          [
            {
              content: t(`pdf.header.${title}`),
              colSpan: columns?.length,
              styles: {
                halign: 'center',
                textColor: 255,
                fillColor: mainColorInRGB,
              },
            },
          ],
          columns?.map(col => t(`pdf.header.${col}`)),
        ],
        body: handleTableData(data, columns),
        foot,
      });
    },
    [handleTableData, mainColor, mainColorInRGB, t],
  );

  const toPdf = useCallback(async () => {
    setDownloadProgress(true);
    try {
      const { data } = await getInvoiceData();

      setDownloadProgress(false);

      const doc = new JsPDF();

      header(doc, data);

      const recurrentItems = data?.itens?.itensRecorrentes;
      const activationItems = data?.itens?.itensAtivacoes;
      const proRataItems = data?.itens?.itensProRata;
      const otherItems = data?.itens?.itensOutros;

      createTable({
        doc,
        title: 'monthlyPayment',
        columns: [
          'dsOperadora',
          'dsItem',
          'nuValorUnitario',
          'nuQtdItem',
          'nuValorTotal',
        ],
        data: recurrentItems?.listaItensRecorrentes,
        foot: [
          [
            {
              // @ts-ignore
              content: t('pdf.header.subtotal'),
              colSpan: 3,
            },
            {
              content: recurrentItems?.nuQtdItemSubTotal ?? 0,
            },
            {
              content: formatCurrency(recurrentItems?.nuValorSubTotal ?? 0),
            },
          ],
        ],
      });
      createTable({
        doc,
        title: 'activations',
        columns: [
          'dsOperadora',
          'nuValorUnitario',
          'nuQtdItem',
          'nuValorTotal',
        ],
        data: activationItems?.listaItensAtivacoes,
        foot: [
          [
            {
              // @ts-ignore
              content: t('pdf.header.subtotal'),
              colSpan: 2,
            },
            {
              content: activationItems?.nuQtdItemSubTotal ?? 0,
            },
            {
              content: formatCurrency(activationItems?.nuValorSubTotal ?? 0),
            },
          ],
        ],
      });
      createTable({
        doc,
        title: 'proportional',
        columns: ['dsOperadora', 'nuQtdItem', 'nuValorTotal'],
        data: proRataItems?.listaItensProRata,
        foot: [
          [
            {
              content: t('pdf.header.subtotal'),
            },
            {
              // @ts-ignore
              content: proRataItems?.nuQtdItemSubTotal ?? 0,
            },
            {
              content: formatCurrency(proRataItems?.nuValorSubTotal ?? 0),
            },
          ],
        ],
      });
      createTable({
        doc,
        title: 'others',
        columns: ['dsItem', 'nuQtdItem', 'nuValorTotal'],
        data: otherItems?.listaItensOutros,
        foot: [
          [
            {
              // @ts-ignore
              content: t('pdf.header.subtotal'),
              colSpan: 2,
            },
            {
              content: formatCurrency(otherItems?.nuValorSubTotal ?? 0),
            },
          ],
          [
            {
              content: t('pdf.header.totalInvoiceValue'),
              colSpan: 2,
            },
            {
              content: formatCurrency(data.nuValorFatura ?? 0),
            },
          ],
        ],
      });

      // @ts-ignore
      doc.lastAutoTable.finalY > 260 && doc.addPage();

      footer(doc, data?.customer);

      const fileName = `${data.dsRazaoSocial}_${data.dataVencimento}.pdf`;

      doc.save(fileName);
    } catch (error) {
      message.error(t('errors.exporting'));
    }
  }, [getInvoiceData, header, createTable, t, footer]);

  return (
    <Tooltip title={downloadProgress && t('pdf.preparing-the-invoice-pdf')}>
      <Button
        type="primary"
        icon={<DownloadOutlined />}
        onClick={toPdf}
        disabled={downloadProgress}
        loading={downloadProgress}
        style={{ marginRight: 8 }}
      >
        {t('pdfExport')}
      </Button>
    </Tooltip>
  );
};

export default PdfDetailedInvoice;
